// ─── CICLU MENSTRUAL ─────────────────────────────────────────────────────────
// Tab-ul de ciclu menstrual: calendar, faze, predicții, statistici, notificări,
// plus modalul de editare a unei zile (DayAddSection) și calendarul de perioadă.
import React from "react";
import { t } from './i18n';
import { fmConfirm } from './dialog';
import { addDaysISO, todayISO } from './helpers';
import { CYCLE_SYMPTOMS, WDAYS, MSHORT } from './data';
import { Toggle } from './ui';

// ─── CICLU MENSTRUAL ─────────────────────────────────────────────────────────
// Simptome relevante pentru ciclul menstrual (folosite în modalul de editare zi din calendar)
export function CicluTab({cycleData, setCycleData, setModal}) {
  const data = cycleData || {};
  const periods = data.periods || [];
  const intercourse = data.intercourse || {};
  const notes = data.notes || {};
  const customSymptoms = data.customSymptoms || [];
  const smartPeriod = data.smartPeriod !== false;
  const smartCycle = data.smartCycle !== false;
  const ignoreIrregular = data.ignoreIrregular === true;
  const [viewMonth, setViewMonth] = React.useState(()=>{ const n=new Date(); return {y:n.getFullYear(), m:n.getMonth()}; });
  const [selectedIso, setSelectedIso] = React.useState(()=>todayISO());
  const [editMode, setEditMode] = React.useState(false);
  const [openSection, setOpenSection] = React.useState(null);
  React.useEffect(()=>{ setOpenSection(null); }, [selectedIso]);
  function isoOf(dt){ const y=dt.getFullYear(),m=String(dt.getMonth()+1).padStart(2,"0"),d=String(dt.getDate()).padStart(2,"0"); return `${y}-${m}-${d}`; }
  function addDays(iso,n){ const[y,m,d]=iso.split("-").map(Number); const dt=new Date(y,m-1,d); dt.setDate(dt.getDate()+n); return isoOf(dt); }
  // Numără zilele între două date ISO (calendaristic, fără probleme de fus orar)
  function daysBetween(a,b){ const[y1,m1,d1]=a.split("-").map(Number); const[y2,m2,d2]=b.split("-").map(Number); return Math.round((new Date(y2,m2-1,d2)-new Date(y1,m1-1,d1))/(864e5)); }
  function fmtNice(iso){ if(!iso) return "—"; const[,m,d]=iso.split("-"); return `${parseInt(d)} ${t(MSHORT[parseInt(m)-1])}.`; }
  const todayStr=isoOf(new Date());
  function mean(arr){ return arr.length?Math.round(arr.reduce((s,x)=>s+x,0)/arr.length):null; }
  // Grupează zilele de menstruație în SERII consecutive (fiecare serie = o menstruație)
  function periodRuns(){
    const sorted=[...new Set(periods)].sort();
    if(!sorted.length) return [];
    const runs=[]; let run=[sorted[0]];
    for(let i=1;i<sorted.length;i++){
      if(addDays(sorted[i-1],1)===sorted[i]) run.push(sorted[i]);
      else { runs.push(run); run=[sorted[i]]; }
    }
    runs.push(run);
    return runs;
  }
  const _runs = periodRuns();
  const _starts = _runs.map(r=>r[0]);
  // Lungimea ciclului = media intervalelor dintre începuturile de serii consecutive (doar valori plauzibile)
  function computeCycleLen(){
    if(!smartCycle) return data.manualCycleLen || 28;
    const diffs=[];
    for(let i=1;i<_starts.length;i++){
      const diff=daysBetween(_starts[i-1],_starts[i]);
      if(diff>=15&&diff<=45) diffs.push(diff);
    }
    if(diffs.length===0) return data.manualCycleLen || 28;
    let use=diffs;
    // "Cicluri neregulate" pornit: exclude doar ciclurile clar atipice (în afara intervalului normal 21-35 zile).
    // Variațiile normale (ex. un ciclu de 24 sau 32) NU se exclud — sunt fiziologice.
    if(ignoreIrregular && diffs.length>=3){
      const normal=diffs.filter(d=>d>=21&&d<=35);
      use = normal.length ? normal : diffs;
    }
    return mean(use) || data.manualCycleLen || 28;
  }
  // Durata menstruației = media lungimilor seriilor complete (exclude seria în curs și seriile de 1 zi)
  function computePeriodLen(){
    if(!smartPeriod) return data.manualPeriodLen || 5;
    if(_runs.length===0) return data.manualPeriodLen || 5;
    let useRuns=_runs;
    const lastRun=_runs[_runs.length-1];
    const lastDay=lastRun[lastRun.length-1];
    if((lastDay===todayStr||lastDay===addDays(todayStr,-1))&&_runs.length>1) useRuns=_runs.slice(0,-1);
    const lens=useRuns.map(r=>r.length).filter(l=>l>=2);
    if(lens.length===0) return data.manualPeriodLen || 5;
    return mean(lens) || data.manualPeriodLen || 5;
  }
  function computeLutealLen(){ return data.manualLutealLen || 14; }
  const cycleLen=computeCycleLen();
  const periodLen=computePeriodLen();
  const lutealLen=computeLutealLen();
  // Ancora: cel mai recent început REAL de menstruație (sau cycleStartDate dacă e mai recent ȘI încă există în periods)
  const _realLast = _starts.length ? _starts[_starts.length-1] : null;
  // cycleStartDate e valid doar dacă acea zi chiar mai există marcată (nu a fost ștearsă)
  const _validStartDate = data.cycleStartDate && periods.includes(data.cycleStartDate) ? data.cycleStartDate : null;
  // Începutul seriei de menstruație care include azi (dacă azi e marcat ca menstruație)
  function periodRunStartContaining(iso){
    if(!periods.includes(iso)) return null;
    let s=iso;
    while(periods.includes(addDays(s,-1))) s=addDays(s,-1);
    return s;
  }
  const _todayRunStart = periodRunStartContaining(todayStr);
  let _anchor = _realLast;
  if(_validStartDate && (!_realLast || _validStartDate >= _realLast)) _anchor = _validStartDate;
  // Menstruația e activă ACUM doar dacă realitatea o confirmă:
  //  (a) azi e marcat ca menstruație, SAU
  //  (b) ieri a fost marcat ȘI seria încă nu a atins durata normală (periodLen) — deci poate continua azi.
  // Dacă seria de ieri a atins deja durata normală, menstruația s-a terminat.
  // Nu ne bazăm pe flag-ul periodActive (poate rămâne blocat). Calculul vine din datele reale,
  // indiferent cum confirmi finalizarea (buton sau calendar).
  function runLengthFrom(startIso){ let len=0,c=startIso; while(periods.includes(c)){ len++; c=addDays(c,1); } return len; }
  const _yesterday = addDays(todayStr,-1);
  let _continuesFromYesterday = false;
  if(periods.includes(_yesterday)){
    const _yRunStart = periodRunStartContaining(_yesterday);
    if(_yRunStart && runLengthFrom(_yRunStart) < periodLen) _continuesFromYesterday = true;
  }
  const inActivePeriodReal = !!_todayRunStart || _continuesFromYesterday;
  // Dacă azi e marcat ca menstruație, ancora devine începutul seriei curente
  if(_todayRunStart) _anchor = _todayRunStart;
  else if(_continuesFromYesterday) _anchor = periodRunStartContaining(_yesterday) || _anchor;
  // Începutul ciclului CURENT: pornind de la ancoră, avansează cu câte un cycleLen
  // cât timp există un ciclu mai recent care a început deja (≤ azi).
  // Excepție: dacă menstruația e activă, ancora rămâne fixă (pentru "Ziua X").
  let lastStart = _anchor;
  if(lastStart && !inActivePeriodReal){
    while(addDays(lastStart, cycleLen) <= todayStr){
      lastStart = addDays(lastStart, cycleLen);
    }
  }
  // Fereastra fertilă: offset-uri (zile de la începutul ciclului) pentru start și sfârșit
  // Cicluri regulate: ovulație(cycleLen-lutealLen) - 5 zile ... +1 zi
  // Cicluri neregulate (≥3 cicluri, variație >4 zile): cel mai scurt-18 ... cel mai lung-11
  function fertileOffsets(){
    // Ovulația are loc cu lutealLen zile ÎNAINTE de următoarea menstruație.
    // Ziua ovulației (numărată de la ziua 1 = prima zi de menstruație) = cycleLen - lutealLen.
    // Ca offset 0-indexat de la începutul ciclului: cycleLen - lutealLen - 1.
    // Ex: ciclu 28, luteală 14 → ovulație ziua 14 (offset 13). Ciclu 32 → ziua 18.
    const ovOffset = cycleLen - lutealLen - 1;
    // Fereastra fertilă: 5 zile înainte de ovulație (sperma trăiește ~5 zile) + ovulația + 1 zi după = 7 zile.
    const fStartOff = Math.max(0, ovOffset - 5);
    const fEndOff = ovOffset + 1;
    return {fStartOff, fEndOff, ovOffset};
  }
  let nextPeriod=null,ovulation=null,fertileStart=null,daysLeft=null,cycleDay=null;
  if(lastStart){
    const {fStartOff,ovOffset}=fertileOffsets();
    // Următoarea menstruație se calculează de la ULTIMA menstruație reală (_realLast),
    // nu de la lastStart (care avansează prin predicție). Astfel, dacă nu marchezi menstruația
    // când e așteptată, daysLeft devine negativ → header-ul arată corect "X zile întârziere".
    const _baseForNext = _realLast || lastStart;
    nextPeriod=addDays(_baseForNext,cycleLen);
    // dacă următoarea prezisă a trecut deja cu mult (peste un ciclu) și tot nu s-a marcat,
    // nu sărim înainte — rămânem la prima dată așteptată, ca să reflectăm întârzierea reală.
    ovulation=addDays(lastStart,ovOffset);
    fertileStart=addDays(lastStart,fStartOff);
    daysLeft=daysBetween(todayStr,nextPeriod);
    // Aceeași sursă unică de adevăr ca în calendar: cycleDayOf (definită mai jos).
    cycleDay=cycleDayOf(todayStr);
  }
  // Persistă data următoarei menstruații calculată aici, ca motorul de notificări să
  // folosească EXACT numărul afișat în tab (nu o predicție separată).
  React.useEffect(()=>{
    if(nextPeriod && data.predictedNext!==nextPeriod){
      setCycleData(prev=>({...(prev||{}), predictedNext: nextPeriod}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPeriod]);
  // Header-ul arată "Ziua X" DOAR dacă menstruația e marcată real (buton sau calendar).
  // Dacă predicția zice că ar trebui să fie menstruație dar nu ai marcat-o, arătăm întârzierea
  // (prin daysLeft negativ), nu "Ziua X" — realitatea marcată e singura sursă.
  const showCycleDay = inActivePeriodReal;
  function inRange(iso,a,b){ return a&&b&&iso>=a&&iso<=b; }
  // Predicții: pentru ciclurile REALE din trecut, fereastra fertilă/ovulația se calculează
  // față de fiecare început real. Pentru VIITOR, se prezic cicluri pornind de la lastStart.
  function allCyclePredictions(){
    if(!lastStart) return [];
    const {fStartOff,fEndOff,ovOffset}=fertileOffsets();
    const result=[];
    // 1) Cicluri REALE din trecut: pentru fiecare început real de menstruație,
    //    calculăm ovulația și fereastra fertilă a acelui ciclu (nu prezicem menstruația — ea e deja reală)
    for(const s of _starts){
      result.push({
        pStart:null, pEnd:null, // menstruația reală e deja marcată ca "period"
        ov:addDays(s, ovOffset),
        fStart:addDays(s, fStartOff),
        fEnd:addDays(s, fEndOff),
        real:true
      });
    }
    // 2) Cicluri PREZISE în viitor: pornind de la următorul ciclu după lastStart
    for(let offset=1; offset<=36; offset++){
      const cycleStart=addDays(lastStart, offset*cycleLen);
      result.push({
        pStart:cycleStart,
        pEnd:addDays(cycleStart, periodLen-1),
        ov:addDays(cycleStart, ovOffset),
        fStart:addDays(cycleStart, fStartOff),
        fEnd:addDays(cycleStart, fEndOff),
        real:false
      });
    }
    // 3) Ciclul CURENT (de la lastStart): dacă lastStart e o predicție (nu un start real),
    //    prezicem și menstruația lui; dacă e real, e deja marcat.
    const lastStartIsReal = _starts.includes(lastStart);
    if(!lastStartIsReal){
      result.push({
        pStart:lastStart,
        pEnd:addDays(lastStart, periodLen-1),
        ov:addDays(lastStart, ovOffset),
        fStart:addDays(lastStart, fStartOff),
        fEnd:addDays(lastStart, fEndOff),
        real:false
      });
    } else {
      // lastStart real → adăugăm doar fertilitatea ciclului curent (menstruația e marcată)
      result.push({
        pStart:null, pEnd:null,
        ov:addDays(lastStart, ovOffset),
        fStart:addDays(lastStart, fStartOff),
        fEnd:addDays(lastStart, fEndOff),
        real:true
      });
    }
    return result;
  }
  function dayType(iso){
    if(periods.includes(iso)) return "period";
    if(!lastStart) return null;
    const preds=allCyclePredictions();
    for(const c of preds){
      if(iso===c.ov) return "ovulation";
      if(inRange(iso,c.fStart,c.fEnd)) return "fertile";
      if(c.pStart && inRange(iso,c.pStart,c.pEnd)) return "predicted";
    }
    return null;
  }
  function setField(field,val){ setCycleData(prev=>({...(prev||{}), [field]:val})); }
  // faza curentă pentru header
  function currentPhaseLabel(){
    const t=dayType(todayStr);
    if(t==="period") return "Menstruație";
    if(t==="ovulation") return "Ovulație";
    if(t==="fertile") return "Perioadă fertilă";
    if(t==="predicted") return "Menstruație";
    if(!lastStart) return "";
    if(cycleDay && cycleDay<=5) return "Faza menstruală";
    if(cycleDay && cycleDay<=13) return "Faza foliculară";
    if(cycleDay && cycleDay<=16) return "Ovulație";
    return "Faza luteală";
  }
  // info ziua selectată
  const selType=dayType(selectedIso);
  // Ziua ciclului la care cade ovulația — derivată din aceeași sursă ca fereastra fertilă (fertileOffsets).
  // ovOffset (0-indexat) + 1 = ziua ciclului. ovOffset = cycleLen - lutealLen - 1, deci ziua = cycleLen - lutealLen.
  const _ovDay = fertileOffsets().ovOffset + 1;
  // Ziua în cadrul ciclului propriu al zilei selectate.
  // Găsește începutul ciclului care conține acea zi: cel mai recent start REAL ≤ iso,
  // iar dacă iso e după lastStart, avansează prin predicție cu cycleLen.
  function cycleStartContaining(iso){
    if(!_starts.length || !iso) return null;
    // cel mai recent început real ≤ iso
    let base=null;
    for(const s of _starts){ if(s<=iso) base=s; else break; }
    if(!base) return null; // iso e înainte de prima menstruație
    // dacă iso e mult după base, avansează prin predicție cât timp ciclul următor a început deja
    while(addDays(base, cycleLen) <= iso){ base=addDays(base, cycleLen); }
    return base;
  }
  function cycleDayOf(iso){
    const base=cycleStartContaining(iso);
    if(!base) return null;
    const diff=daysBetween(base,iso);
    if(diff<0) return null;
    return diff + 1;
  }
  // Numele fazei pentru ziua selectată (fără text explicativ)
  function selPhaseLabel(){
    if(selType==="period"||selType==="predicted") return "Faza menstruală";
    if(selType==="ovulation") return "Ovulația";
    const cd=cycleDayOf(selectedIso);
    if(cd&&cd>0){
      if(cd<=periodLen) return "Faza menstruală";
      if(cd===_ovDay) return "Ovulația";
      if(cd < _ovDay) return "Faza foliculară";
      return "Faza luteală";
    }
    return "";
  }
  // Șansele de a rămâne însărcinată — pentru orice zi a ciclului
  // Mare = 2 zile înainte de ovulație + ziua ovulației; Mediu = restul ferestrei fertile; Redus = în rest
  function selFertilityChance(){
    const cd=cycleDayOf(selectedIso);
    if(cd==null||cd<=0) return "";
    const inFertile = selType==="fertile"||selType==="ovulation";
    // Mare = 2 zile înainte de ovulație + ziua ovulației + 1 zi după
    if(selType==="ovulation" || (cd>=_ovDay-2 && cd<=_ovDay+1)) return "Mare – Șanse de a rămâne însărcinată";
    if(inFertile) return "Mediu – Șanse de a rămâne însărcinată";
    return "Redus – Șanse de a rămâne însărcinată";
  }
  function selDayDotColor(){
    if(selType==="period") return "#e8849b";
    if(selType==="predicted") return "#f6a8c0";
    if(selType==="ovulation") return "#f5a94a";
    if(selType==="fertile") return "#c4a020";
    return "var(--purple)";
  }
  // calendar grid
  const firstDow=(new Date(viewMonth.y,viewMonth.m,1).getDay()+6)%7;
  const daysInMonth=new Date(viewMonth.y,viewMonth.m+1,0).getDate();
  const cells=[];
  for(let i=0;i<firstDow;i++) cells.push(null);
  for(let d=1;d<=daysInMonth;d++) cells.push(d);
  // OvulIcon pastel pentru card — rotund, aceeași culoare ca în calendar (#f5a060)
  function OvulIconPastel({size=28}){
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" style={{display:"inline-block",verticalAlign:"middle"}}>
        <circle cx="10" cy="10" r="9" fill="#f9c89b"/>
        <circle cx="7" cy="7" r="3.5" fill="#fff" opacity="0.85"/>
      </svg>
    );
  }
  function PlantIconPastel({size=28}){
    return (
      <svg width={size} height={size} viewBox="3 5 18 18" style={{display:"inline-block",verticalAlign:"middle"}}>
        {/* movilă de pământ (sol) - mai îngustă */}
        <path d="M6 20 C6 17.5 9 16.5 12 16.5 C15 16.5 18 17.5 18 20 Z" fill="#9c6b3f"/>
        <path d="M7.5 19 C9.5 18.3 10.5 18.4 12 18.5 M12 18.5 C13.5 18.4 14.5 18.3 16.5 19" stroke="#7a5230" strokeWidth="0.6" fill="none" opacity="0.6"/>
        {/* tulpină */}
        <path d="M12 16.5 V8" stroke="#5fa83f" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
        {/* frunză stânga */}
        <path d="M12 11.5 C8.5 11 6.5 12 6 9.5 C9 8 11.2 9 12 11.5 Z" fill="#7bc356"/>
        {/* frunză dreapta */}
        <path d="M12 9.5 C15.5 9 17.5 10 18 7.5 C15 6 12.8 7 12 9.5 Z" fill="#8fd166"/>
        {/* lăstar vârf */}
        <circle cx="12" cy="7.5" r="1.3" fill="#a3e07f"/>
      </svg>
    );
  }
  // Segmentele cercului de faze pentru cardul "Ziua ciclului"
  // Culori consistente cu calendarul: menstruație #e8607a, fertilă galben pal
  function phaseRingSegments(){
    const L = cycleLen || 28;
    const ovDay = L - lutealLen; // ziua ovulației (offset de la start)
    // Folosește numărul REAL de zile de menstruație marcate în ciclul curent,
    // nu media periodLen. Dacă ciclul curent are 3 zile marcate, segmentul roșu = 3 zile.
    const realPeriodDays = (()=>{
      if(!lastStart) return periodLen;
      let n=0, cur=lastStart;
      while(periods.includes(cur)){ n++; cur=addDays(cur,1); }
      return n>0 ? n : periodLen; // dacă nu e marcat nimic în ciclul curent, folosește media
    })();
    const periodEnd = Math.min(realPeriodDays, L);
    const fertileStartD = Math.max(0, ovDay - 5);
    const fertileEndD = Math.min(L, ovDay + 1);
    const segs = [];
    const push=(from,to,color)=>{ if(to>from) segs.push({from,to,color}); };
    push(0, periodEnd, "#e8607a");                    // menstruație (zile reale marcate)
    push(periodEnd, fertileStartD, "#d4c5ed");        // foliculară - violet pal vizibil
    push(fertileStartD, fertileEndD, "#f0d060");      // fertilă+ovulație - galben vizibil
    push(fertileEndD, L, "#d4c5ed");                  // luteală - violet pal vizibil
    let acc=0;
    return segs.map((s,i)=>{
      const lenDays=(s.to-s.from);
      const offsetDays=acc;
      acc+=(s.to-s.from);
      return {lenDays, offsetDays, color:s.color, key:i};
    });
  }
  // Poziția punctului mobil pe cerc, în funcție de ziua ciclului
  function cycleDotPos(){
    const L=cycleLen||28;
    const dayInCycle = cycleDay&&cycleDay>0 ? ((cycleDay-1)%L) : 0;
    const frac=dayInCycle/L;
    // unghiul: pornește din vârf (-90°) și merge în sensul acelor
    const ang=(frac*360-90)*Math.PI/180;
    return { x:22+18*Math.cos(ang), y:22+18*Math.sin(ang) };
  }
  const selParts=selectedIso.split("-");
  const selLabel=`${parseInt(selParts[2])} ${t(MSHORT[parseInt(selParts[1])-1])}.`;
  return (
    <div style={{margin:"0 -16px",background:"var(--bg)"}}>
      {/* ── HEADER ────────────────────────────────────────────────── */}
      <div style={{
        background:"var(--cycle-header)",
        padding:"20px 16px 0"
      }}>
        {/* faza curentă */}
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:13,color:"var(--cycle-hlabel)",letterSpacing:.5,marginBottom:2}}>{t(currentPhaseLabel())}</div>
          {lastStart ? (
            <>
              <div style={{fontSize:30,fontWeight:700,color:"var(--cycle-htext)",lineHeight:1.1}}>
                {showCycleDay
                  ? `${t("Ziua")} ${cycleDay}`
                  : daysLeft>0 ? `${daysLeft} ${daysLeft===1?t("zi rămasă"):t("zile rămase")}`
                  : (()=>{ const intarz=Math.abs(daysLeft)+1; return `${intarz} ${intarz===1?t("zi"):t("zile")} ${t("întârziere")}`; })()}
              </div>
            </>
          ) : (
            <div style={{fontSize:16,color:"var(--cycle-hlabel)",margin:"8px 0"}}>{t("Loghează prima menstruație")}</div>
          )}
        </div>
        {/* buton început / sfârșit menstruație */}
        <div style={{textAlign:"center",padding:"14px 0 16px"}}>
          {(() => {
            // "Începutul menstruației" apare când e momentul să vină (cu 3 zile înainte de data prezisă
            // de la ULTIMA menstruație reală) și RĂMÂNE așa până o marchezi efectiv — indiferent cât timp trece.
            // Nu se bazează pe predicția care avansează automat, ci pe ultima menstruație reală marcată.
            // Astfel, dacă nu marchezi, butonul nu se transformă singur în "Editare".
            let nearPeriod = false;
            if(!inActivePeriodReal && _realLast){
              const daysSinceRealStart = daysBetween(_realLast, todayStr);
              // de la (cycleLen - 3) zile după ultima menstruație reală încolo → așteptăm marcarea
              if(daysSinceRealStart >= cycleLen - 3) nearPeriod = true;
            }
            const editing = !nearPeriod;
            return (
              <button style={{
                background: editing
                  ? "linear-gradient(90deg,#7c3aed,#5b21b6)"
                  : "linear-gradient(90deg,#e8849b,#c4607a)",
                color:"#fff",border:"none",borderRadius:24,
                padding:"10px 32px",fontSize:14,fontWeight:600,cursor:"pointer",
                boxShadow: editing
                  ? "0 2px 14px rgba(124,58,237,.4)"
                  : "0 2px 14px rgba(232,132,155,.4)"
              }} onClick={()=>{
                setModal&&setModal({type: editing?"periodEnd":"periodStart", data:{cycleData,setCycleData,periodLen,cycleLen,lutealLen,lastStart}});
              }}>
                {editing ? t("Editare") : t("Începutul menstruației")}
              </button>
            );
          })()}
        </div>
        {/* ── 4 carduri pe un rând, se micșorează ca să încapă pe ecran ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,paddingBottom:16}}>
          {/* Card 1 — Ziua ciclului */}
          <div style={{background:"#f5f0ff",borderRadius:12,padding:"8px 6px",position:"relative",minHeight:92,display:"flex",flexDirection:"column",justifyContent:"space-between"}}>
            <div style={{fontSize:10,fontWeight:600,color:"#6c3fc9"}}>{t("Ziua ciclului")}</div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,marginTop:4}}>
              <div style={{position:"relative",width:50,height:50,flexShrink:0}}>
                <svg viewBox="0 0 44 44" style={{width:50,height:50}}>
                  <g transform="rotate(-90 22 22)">
                    <circle cx="22" cy="22" r="18" fill="none" stroke="#e0d4f7" strokeWidth="3.5"/>
                    {(()=>{ const L=cycleLen||28; let acc=0; return phaseRingSegments().map((seg,i)=>{
                      // pathLength=100 normalizează circumferința la 100 unități, indiferent de rază
                      const segLen = seg.lenDays/L*100;
                      const gap = 100 - segLen;
                      const dashoffset = -(acc/L*100);
                      acc += seg.lenDays;
                      return <circle key={i} cx="22" cy="22" r="18" fill="none" pathLength="100"
                        stroke={seg.color} strokeWidth="3.5"
                        strokeDasharray={`${segLen} ${gap}`}
                        strokeDashoffset={dashoffset}/>;
                    }); })()}
                  </g>
                  {cycleDay&&cycleDay>0&&(()=>{ const p=cycleDotPos(); return (
                    <circle cx={p.x} cy={p.y} r="2.6" fill="#fff" stroke="#e8607a" strokeWidth="1.6"/>
                  );})()}
                </svg>
                <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:"#6c3fc9"}}>
                  {cycleDay||"—"}
                </div>
              </div>
            </div>
          </div>
          {/* Card 2 — Următoarea menstruație */}
          <div style={{background:"#fff0f3",borderRadius:12,padding:"8px 6px",position:"relative",minHeight:92,display:"flex",flexDirection:"column",justifyContent:"flex-start"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>{fmtNice(nextPeriod)}</div>
            <div style={{fontSize:9,color:"#c06080",lineHeight:1.3,marginTop:2}}>{t("Următoarea")}<br/>{t("menstruație")}</div>
            <div style={{position:"absolute",bottom:8,right:10}}>
              <svg width="22" height="28" viewBox="0 0 26 32">
                <path d="M13 2 C13 2 2 14 2 21 a11 11 0 0 0 22 0 C24 14 13 2 13 2Z" fill="#f48aaa" opacity="0.7"/>
              </svg>
            </div>
          </div>
          {/* Card 3 — Perioada fertilă */}
          <div style={{background:"#fffde7",borderRadius:12,padding:"8px 6px",position:"relative",minHeight:92,display:"flex",flexDirection:"column",justifyContent:"flex-start"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>{fmtNice(fertileStart)}</div>
            <div style={{fontSize:9,color:"#8a7020",lineHeight:1.3,marginTop:2}}>{t("Perioada")}<br/>{t("fertilă")}</div>
            <div style={{position:"absolute",bottom:6,right:6,opacity:.7}}><PlantIconPastel size={30}/></div>
          </div>
          {/* Card 4 — Ovulație */}
          <div style={{background:"#fff8f0",borderRadius:12,padding:"8px 6px",position:"relative",minHeight:92,display:"flex",flexDirection:"column",justifyContent:"flex-start"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>{fmtNice(ovulation)}</div>
            <div style={{fontSize:9,color:"#9a6030",lineHeight:1.3,marginTop:2}}>{t("Ovulație")}</div>
            <div style={{position:"absolute",bottom:6,right:8}}>
              <OvulIconPastel size={28}/>
            </div>
          </div>
        </div>
      </div>
      {/* ── CALENDAR ──────────────────────────────────────────────── */}
      <div style={{background:"var(--cycle-card)",padding:"0 16px"}}>
        <div style={{maxWidth:420,margin:"0 auto"}}>
        {/* nav lună */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0 8px"}}>
          <button style={{background:"none",border:"none",color:"var(--purple)",fontSize:22,cursor:"pointer",padding:"0 6px"}}
            onClick={()=>setViewMonth(v=>{const m=v.m-1;return m<0?{y:v.y-1,m:11}:{y:v.y,m};})}>‹</button>
          <div style={{fontWeight:700,fontSize:16,color:"var(--text)"}}>{t(MSHORT[viewMonth.m])}. {viewMonth.y}</div>
          <button style={{background:"none",border:"none",color:"var(--purple)",fontSize:22,cursor:"pointer",padding:"0 6px"}}
            onClick={()=>setViewMonth(v=>{const m=v.m+1;return m>11?{y:v.y+1,m:0}:{y:v.y,m};})}>›</button>
        </div>
        {/* banner mod editare */}
        {editMode && (
          <div style={{textAlign:"center",marginBottom:8,padding:"7px 12px",background:"var(--cycle-card)",borderRadius:10,fontSize:12,color:"#f48aaa",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <svg width="11" height="13" viewBox="0 0 26 32">
              <path d="M13 2 C13 2 2 14 2 21 a11 11 0 0 0 22 0 C24 14 13 2 13 2Z" fill="#f48aaa"/>
            </svg>
            {t("Atingeți data pentru a ajusta menstruația")}
            <button style={{background:"#e8849b",color:"#fff",border:"none",borderRadius:12,padding:"3px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}
              onClick={()=>setEditMode(false)}>{t("Salvează")}</button>
          </div>
        )}
        {/* antete zile săptămână */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:2}}>
          {WDAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"#6b6080",fontWeight:700,padding:"4px 0"}}>{t(d)}</div>)}
        </div>
        {/* grid zile */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
          {cells.map((d,i)=>{
            if(d===null) return <div key={i} style={{aspectRatio:"1"}}/>;
            const iso=`${viewMonth.y}-${String(viewMonth.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const type=dayType(iso);
            const isToday=iso===todayStr;
            const isSel=iso===selectedIso;
            const hasIC=!!intercourse[iso];
            const hasNote=!!notes[iso];
            const cellCycDay=cycleDayOf(iso);
            let cellBg="transparent", dayNumColor="var(--purple-2)", cycDayColor="var(--text-4)";
            if(type==="period"){       cellBg="#e8607a"; dayNumColor="#fff";    cycDayColor="rgba(255,255,255,0.6)"; }
            else if(type==="predicted"){cellBg="#f5c0d0"; dayNumColor="#c04060"; cycDayColor="#c06070"; }
            else if(type==="fertile"){  cellBg="#fefce8"; dayNumColor="#1a1a2e"; cycDayColor="#8a8040"; }
            else if(type==="ovulation"){cellBg="#fefce8"; dayNumColor="#1a1a2e"; cycDayColor="#8a8040"; }
            return (
              <button key={i}
                onClick={()=>{ if(editMode){ setCycleData(prev=>{ const p=prev||{}; const list=p.periods||[]; return {...p,periods:list.includes(iso)?list.filter(x=>x!==iso):[...list,iso]}; }); } else { setSelectedIso(iso); } }}
                style={{
                  position:"relative",aspectRatio:"1",
                  background:cellBg,
                  border: isSel ? "2px solid #7c3aed" : isToday ? "2px solid #7c3aed66" : "1px solid var(--cycle-border)",
                  cursor:"pointer",padding:"2px 3px",
                  display:"flex",flexDirection:"column",justifyContent:"space-between",
                  fontFamily:"inherit",boxSizing:"border-box",borderRadius:8
                }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",minHeight:11}}>
                  <div>
                    {type==="ovulation" && (
                      <svg width="8" height="8" viewBox="0 0 20 20" style={{display:"block"}}>
                        <circle cx="10" cy="10" r="9" fill="#f9c89b"/>
                        <circle cx="7" cy="7" r="3.5" fill="#fff" opacity="0.85"/>
                      </svg>
                    )}
                    {type==="fertile" && (
                      <svg width="8" height="8" viewBox="4 5 16 16" style={{display:"block"}}>
                        <path d="M6 20 C6 17.5 9 16.5 12 16.5 C15 16.5 18 17.5 18 20 Z" fill="#9c6b3f"/>
                        <path d="M12 16.5 V8" stroke="#5fa83f" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                        <path d="M12 11.5 C8.5 11 6.5 12 6 9.5 C9 8 11.2 9 12 11.5 Z" fill="#7bc356"/>
                        <path d="M12 9.5 C15.5 9 17.5 10 18 7.5 C15 6 12.8 7 12 9.5 Z" fill="#8fd166"/>
                        <circle cx="12" cy="7.5" r="1.3" fill="#a3e07f"/>
                      </svg>
                    )}
                  </div>
                  <div style={{fontSize:7,color:cycDayColor,lineHeight:1}}>
                    {cellCycDay&&cellCycDay>0?cellCycDay:""}
                  </div>
                </div>
                <div style={{fontSize:11,fontWeight:isToday?700:500,color:dayNumColor,lineHeight:1,textAlign:"left"}}>
                  {d}
                </div>
                {(hasIC||hasNote)&&(
                  <div style={{position:"absolute",bottom:2,right:3,display:"flex",gap:2}}>
                    {hasIC&&<span style={{fontSize:9}}>❤️</span>}
                    {hasNote&&<span style={{display:"inline-block",width:3,height:3,borderRadius:"50%",background:"var(--purple)"}}/>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        </div>
        {/* legendă */}
        <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:10,fontSize:10,color:"#6b7080",paddingBottom:4}}>
          <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{display:"inline-block",width:9,height:9,borderRadius:2,background:"#e8607a"}}/>{t("Menstruație")}</span>
          <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{display:"inline-block",width:9,height:9,borderRadius:2,background:"#f5c0d0"}}/>{t("Estimat")}</span>
          <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{display:"inline-block",width:9,height:9,borderRadius:2,background:"#fefce8",border:"1px solid #c4a020"}}/>{t("Fertilă + Ovulație")}</span>
        </div>
        {/* ── panou zi selectată ── */}
        <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #2a1e4a",paddingBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div>
              <div style={{fontSize:18,fontWeight:700,color:"#fff"}}>{t(selPhaseLabel())||selLabel}</div>
            </div>
          </div>
          {selFertilityChance()&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:selDayDotColor(),flexShrink:0}}/>
              <span style={{fontSize:13,color:"var(--purple-2)"}}>{t(selFertilityChance())}</span>
            </div>
          )}
          {(intercourse[selectedIso]||notes[selectedIso]||((data.symptoms||{})[selectedIso]||[]).length>0)&&(
            <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
              {intercourse[selectedIso]&&(()=>{const v=intercourse[selectedIso];const o=typeof v==="string"?{protection:v,count:1,orgasm:null}:v;return <span onClick={()=>setOpenSection("intercourse")} style={{fontSize:11,background:"var(--cycle-surface)",borderRadius:12,padding:"5px 10px",color:"var(--purple)",alignSelf:"flex-start",cursor:"pointer"}}>❤️ {t("Contact intim")}{o.protection?(o.protection==="protected"?" · 🛡️ "+t("Protejat"):" · 🔥 "+t("Neprotejat")):""}{o.count>1?` · ×${o.count}`:""}{o.orgasm===true?" · 🎉":o.orgasm===false?" · 😔":""}</span>;})()}
              {((data.symptoms||{})[selectedIso]||[]).length>0 && (
                <span onClick={()=>setOpenSection("symptoms")} style={{fontSize:11,background:"var(--cycle-surface)",borderRadius:12,padding:"5px 10px",color:"var(--purple-2)",alignSelf:"flex-start",cursor:"pointer"}}>🤒 {((data.symptoms||{})[selectedIso]||[]).join(", ")}</span>
              )}
              {notes[selectedIso]&&<span onClick={()=>setOpenSection("note")} style={{fontSize:11,background:"var(--cycle-surface)",borderRadius:12,padding:"5px 10px",color:"var(--text-2)",alignSelf:"flex-start",cursor:"pointer",maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📝 {notes[selectedIso]}</span>}
            </div>
          )}
          <DayAddSection iso={selectedIso} cycleData={cycleData} setCycleData={setCycleData} customSymptoms={customSymptoms} allSymptoms={CYCLE_SYMPTOMS} openSection={openSection} setOpenSection={setOpenSection}/>
        </div>
      </div>
      {/* ── SETĂRI PREDICȚIE ──────────────────────────────────────── */}
      <div style={{background:"var(--cycle-bg)",padding:"16px",borderTop:"1px solid #2a1e4a"}}>
        <div style={{fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:14}}>{t("Predicție")}</div>
        <div style={{marginBottom:16,paddingBottom:16,borderBottom:"1px solid var(--border)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{t("Lungimea menstruației")}</span>
            <input type="number" value={smartPeriod?periodLen:(data.manualPeriodLen||"")} placeholder={String(periodLen)} disabled={smartPeriod}
              onChange={e=>setField('manualPeriodLen',e.target.value?parseInt(e.target.value):null)}
              style={{width:60,textAlign:"center",background:smartPeriod?"var(--cycle-surface)":"var(--cycle-input)",border:"1px solid #3a2e5a",color:smartPeriod?"#5a5170":"var(--text)",borderRadius:8,padding:"4px 8px",fontFamily:"inherit",cursor:smartPeriod?"not-allowed":"text"}}/>
          </div>
          <Toggle label="Predicție inteligentă" sub="Bazată pe datele introduse de tine. Acuratețea se îmbunătățește cu cât introduci mai multe date."
            on={smartPeriod} onChange={v=>setField('smartPeriod',v)}/>
        </div>
        <div style={{marginBottom:16,paddingBottom:16,borderBottom:"1px solid var(--border)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{t("Lungimea ciclului menstrual")}</span>
            <input type="number" value={smartCycle?cycleLen:(data.manualCycleLen||"")} placeholder={String(cycleLen)} disabled={smartCycle}
              onChange={e=>setField('manualCycleLen',e.target.value?parseInt(e.target.value):null)}
              style={{width:60,textAlign:"center",background:smartCycle?"var(--cycle-surface)":"var(--cycle-input)",border:"1px solid #3a2e5a",color:smartCycle?"#5a5170":"var(--text)",borderRadius:8,padding:"4px 8px",fontFamily:"inherit",cursor:smartCycle?"not-allowed":"text"}}/>
          </div>
          <Toggle label="Predicție inteligentă" sub="Bazată pe datele introduse de tine. Acuratețea se îmbunătățește cu cât introduci mai multe date."
            on={smartCycle} onChange={v=>setField('smartCycle',v)}/>
          <div style={{height:10}}/>
          <Toggle label="Cicluri neregulate" sub="Când este pornit, ciclurile neregulate vor fi ignorate, făcând predicțiile mult mai acurate."
            on={ignoreIrregular} onChange={v=>setField('ignoreIrregular',v)}/>
        </div>
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{t("Lungimea fazei luteale")}</span>
            <input type="number" value={data.manualLutealLen||""} placeholder={t("14")}
              onChange={e=>setField('manualLutealLen',e.target.value?parseInt(e.target.value):null)}
              style={{width:60,textAlign:"center",background:"var(--cycle-input)",border:"1px solid #3a2e5a",color:"var(--text)",borderRadius:8,padding:"4px 8px",fontFamily:"inherit"}}/>
          </div>
          <div style={{fontSize:11,color:"var(--text-2)",lineHeight:1.5}}>
            {t("Intervalul dintre ovulație și menstruație. Standard 14 zile pentru majoritatea persoanelor. Folosită pentru a estima ovulația și fereastra fertilă. Modific-o doar dacă știi valoarea ta exactă (de la medic sau monitorizare).")}
          </div>
        </div>
        {/* Rezumatul calculelor din datele tale */}
        <div style={{marginTop:16,padding:"12px 14px",background:"var(--cycle-surface)",borderRadius:10,border:"1px solid #2a2440"}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--purple-2)",marginBottom:8}}>{t("📊 Calculat din datele tale")}</div>
          <div style={{fontSize:12,color:"var(--text-2)",lineHeight:1.7}}>
            <div>{t("Menstruații înregistrate: ")}<b style={{color:"var(--text)"}}>{_starts.length}</b></div>
            <div>{t("Lungime ciclu: ")}<b style={{color:"var(--text)"}}>{cycleLen} {t("zile")}</b> {smartCycle?t("(calculat automat)"):t("(setat manual)")}</div>
            <div>{t("Durată menstruație: ")}<b style={{color:"var(--text)"}}>{periodLen} {t("zile")}</b> {smartPeriod?t("(calculat automat)"):t("(setat manual)")}</div>
            <div>{t("Fază luteală: ")}<b style={{color:"var(--text)"}}>{lutealLen} {t("zile")}</b></div>
            <div style={{marginTop:4}}>{t("Ovulație estimată: ")}<b style={{color:"var(--text)"}}>{t("ziua")} {_ovDay}</b></div>
            <div>{t("Fereastră fertilă: ")}<b style={{color:"var(--text)"}}>{(()=>{const{fStartOff,fEndOff}=fertileOffsets();return `${t("zilele")} ${fStartOff+1}–${fEndOff+1} (${fEndOff-fStartOff+1} ${t("zile")})`;})()}</b></div>
          </div>
        </div>
      </div>
    </div>
  );
}


export function DayAddSection({iso, cycleData, setCycleData, customSymptoms, allSymptoms, openSection, setOpenSection}) {
  const data = cycleData || {};
  const [sympQuery, setSympQuery] = React.useState("");
  const [confetti, setConfetti] = React.useState(false);
  const [protPulse, setProtPulse] = React.useState(false);
  const [unprotShake, setUnprotShake] = React.useState(false);
  const [sadLeaf, setSadLeaf] = React.useState(false);
  const [statsWin, setStatsWin] = React.useState(30);
  const open = openSection;
  const setOpen = setOpenSection;

  // Valorile salvate (din cycleData) — sursa de adevăr
  const savedRawIc = (data.intercourse||{})[iso] || null;
  const savedIc = savedRawIc==null ? null : (typeof savedRawIc==="string" ? {protection:savedRawIc, count:1, orgasm:null} : savedRawIc);
  const savedSymptoms = (data.symptoms||{})[iso] || [];
  const savedNote = (data.notes||{})[iso] || "";

  // DRAFT local — modificările trăiesc aici până la Salvează
  const [draftIc, setDraftIc] = React.useState(savedIc);
  const [draftSymptoms, setDraftSymptoms] = React.useState(savedSymptoms);
  const [noteVal, setNoteVal] = React.useState(savedNote);

  // La schimbarea zilei: resetează draft-urile la valorile salvate (pierde modificările nesalvate)
  React.useEffect(()=>{
    setDraftIc(savedIc);
    setDraftSymptoms(savedSymptoms);
    setNoteVal(savedNote);
    setSympQuery("");
    // Intenționat doar pe `iso`: resetăm draft-urile la valorile salvate DOAR la schimbarea zilei.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso]);

  // Când deschizi o secțiune, sincronizează draft-ul cu ce e salvat (anularea anterioară nu lasă urme)
  React.useEffect(()=>{
    if(open==="intercourse") setDraftIc(savedIc);
    if(open==="symptoms") setDraftSymptoms(savedSymptoms);
    if(open==="note") setNoteVal(savedNote);
    // Intenționat doar pe `open`: sincronizăm draft-ul cu ce e salvat DOAR la deschiderea secțiunii.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const icObj = draftIc;
  const daySymptoms = draftSymptoms;

  // ─── Contact intim: modifică DOAR draft-ul ───
  function toggleProtection(val){
    const base = icObj || {count:1, orgasm:null, protection:null};
    if(base.protection===val){ setDraftIc({...base, protection:null}); return; }
    setDraftIc({...base, protection:val});
    if(val==="protected"){ setProtPulse(true); setTimeout(()=>setProtPulse(false),700); }
    else { setUnprotShake(true); setTimeout(()=>setUnprotShake(false),600); }
  }
  function setCount(n){ const base=icObj||{protection:null,count:1,orgasm:null}; setDraftIc({...base, count:Math.max(1,n)}); }
  function setOrgasm(v){
    const base=icObj||{protection:null,count:1,orgasm:null};
    const nv = base.orgasm===v ? null : v;
    setDraftIc({...base, orgasm:nv});
    if(nv===true){ setConfetti(true); setTimeout(()=>setConfetti(false), 2800); }
    if(nv===false){ setSadLeaf(true); setTimeout(()=>setSadLeaf(false), 2800); }
  }
  function saveIntercourse(){
    setCycleData(prev=>{ const p=prev||{}; const m={...(p.intercourse||{})}; if(draftIc===null)delete m[iso];else m[iso]=draftIc; return {...p,intercourse:m}; });
    setOpen(null);
  }
  function deleteIntercourse(){
    setDraftIc(null);
    setCycleData(prev=>{ const p=prev||{}; const m={...(p.intercourse||{})}; delete m[iso]; return {...p,intercourse:m}; });
    setOpen(null);
  }

  // ─── Simptome: modifică DOAR draft-ul ───
  function toggleSymptom(s){
    setDraftSymptoms(cur => cur.includes(s) ? cur.filter(x=>x!==s) : [...cur, s]);
  }
  function addCustomSymptom(){
    const v=sympQuery.trim(); if(!v) return;
    // simptomele custom se salvează imediat în listă (sunt globale, nu țin de ziua asta)
    if(!customSymptoms.includes(v) && !allSymptoms.includes(v)){
      setCycleData(prev=>{ const p=prev||{}; const cs=p.customSymptoms||[]; return {...p, customSymptoms:cs.includes(v)?cs:[...cs,v]}; });
    }
    if(!draftSymptoms.includes(v)) setDraftSymptoms(cur=>[...cur, v]);
    setSympQuery("");
  }
  function deleteCustomSymptom(name){
    // ștergerea unui simptom custom din listă e globală — se aplică imediat
    setCycleData(prev=>{
      const p=prev||{};
      const newCs=(p.customSymptoms||[]).filter(s=>s!==name);
      const sm={...(p.symptoms||{})};
      Object.keys(sm).forEach(k=>{ const f=sm[k].filter(x=>x!==name); if(f.length)sm[k]=f; else delete sm[k]; });
      return {...p, customSymptoms:newCs, symptoms:sm};
    });
    setDraftSymptoms(cur=>cur.filter(x=>x!==name));
  }
  function saveSymptoms(){
    setCycleData(prev=>{ const p=prev||{}; const sm={...(p.symptoms||{})}; if(draftSymptoms.length)sm[iso]=draftSymptoms;else delete sm[iso]; return {...p,symptoms:sm}; });
    setOpen(null);
  }

  // ─── Notiță: modifică DOAR draft-ul ───
  function saveNoteSection(){
    setCycleData(prev=>{ const p=prev||{}; const m={...(p.notes||{})}; if(noteVal.trim())m[iso]=noteVal.trim();else delete m[iso]; return {...p,notes:m}; });
    setOpen(null);
  }
  function deleteNote(){
    setNoteVal("");
    setCycleData(prev=>{ const p=prev||{}; const m={...(p.notes||{})}; delete m[iso]; return {...p,notes:m}; });
    setOpen(null);
  }

  const fullList = [...allSymptoms, ...customSymptoms.filter(s=>!allSymptoms.includes(s))];
  const filteredSymps = fullList.filter(s=>{const q=sympQuery.toLowerCase();return t(s).toLowerCase().includes(q);});
  const exactExists = fullList.some(s=>t(s).toLowerCase()===sympQuery.trim().toLowerCase());
  const card = {background:"#160f24",borderRadius:14,padding:"14px",border:"1px solid #2a1f3e",marginTop:10};
  const bigBtn = (active,grad,brd,extra)=>({flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"14px 6px",borderRadius:14,cursor:"pointer",background:active?grad:"var(--bg)",border:active?("2px solid "+brd):"1px solid var(--border-2)",color:"#fff",fontFamily:"inherit",transition:"all .15s",...(extra||{})});
  const trash = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
  const saveBtnStyle = {width:"100%",marginTop:12,fontSize:14,fontWeight:600,padding:"11px",borderRadius:10,cursor:"pointer",fontFamily:"inherit",background:"linear-gradient(90deg,#7c3aed,#5b21b6)",color:"#fff",border:"none"};

  return (
    <div style={{marginTop:12}}>
      <div style={{display:"flex",justifyContent:"center"}}>
        <button onClick={()=>setOpen(open==="menu"?null:"menu")} style={{
          padding:"7px 20px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,
          background:"rgba(124,58,237,0.15)",color:"#b794e8",border:"1px solid rgba(124,58,237,0.35)",
          display:"inline-flex",alignItems:"center",gap:6
        }}>
          {open==="menu"?t("✕ Închide"):t("➕ Adaugă")}
        </button>
      </div>

      {open==="menu" && (
        <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:10}}>
          <button className="bg" style={{width:"100%",fontSize:14,padding:"12px",textAlign:"left"}} onClick={()=>setOpen("symptoms")}>{t("🤒 Simptome")}</button>
          <button className="bg" style={{width:"100%",fontSize:14,padding:"12px",textAlign:"left"}} onClick={()=>setOpen("intercourse")}>{t("💕 Contact intim")}</button>
          <button className="bg" style={{width:"100%",fontSize:14,padding:"12px",textAlign:"left"}} onClick={()=>setOpen("note")}>{t("📝 Notiță")}</button>
        </div>
      )}

      {open==="symptoms" && (
        <div style={card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span className="lbl">{t("🤒 Simptome")}</span>
            <button className="bg" style={{fontSize:12,padding:"4px 10px"}} onClick={()=>setOpen(null)}>{t("Anulează")}</button>
          </div>
          {daySymptoms.length>0 && (
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
              {daySymptoms.map(s=>(
                <div key={s} style={{fontSize:13,background:"#3a2a52",borderRadius:10,padding:"8px 10px",color:"#e6d4ff",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span>{t(s)}</span>
                  <button onClick={()=>toggleSymptom(s)} style={{background:"none",border:"none",color:"#c4a0e0",cursor:"pointer",padding:0,display:"flex",alignItems:"center"}}>{trash}</button>
                </div>
              ))}
            </div>
          )}
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <input value={sympQuery} onChange={e=>setSympQuery(e.target.value)} placeholder={t("Caută sau scrie un simptom...")}
              style={{flex:1,background:"var(--bg)",border:"1px solid var(--border-2)",borderRadius:8,color:"var(--text)",padding:"8px 10px",fontSize:13,fontFamily:"inherit"}}/>
            {sympQuery.trim() && !exactExists && (
              <button className="bp" style={{fontSize:12,padding:"0 14px",whiteSpace:"nowrap"}} onClick={addCustomSymptom}>{t("+ Adaugă")}</button>
            )}
          </div>
          <div style={{maxHeight:200,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
            {filteredSymps.map(s=>{
              const isCustom = customSymptoms.includes(s) && !allSymptoms.includes(s);
              return (
              <div key={s} style={{display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>toggleSymptom(s)} style={{
                  flex:1,textAlign:"left",fontSize:13,padding:"9px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                  background: daySymptoms.includes(s)?"#7c3aed":"var(--bg)",
                  color: daySymptoms.includes(s)?"#fff":"var(--purple-2)",
                  border:"1px solid "+(daySymptoms.includes(s)?"#7c3aed":"var(--border-2)"),
                  display:"flex",alignItems:"center",justifyContent:"space-between"
                }}>
                  <span>{t(s)}</span>
                  {daySymptoms.includes(s) && <span style={{fontSize:14}}>✓</span>}
                </button>
                {isCustom && (
                  <button onClick={()=>deleteCustomSymptom(s)} title={t("Șterge din listă")} style={{
                    background:"var(--cycle-input)",border:"1px solid #3a2e5a",borderRadius:8,color:"#f0a0a0",cursor:"pointer",
                    width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0
                  }}>{trash}</button>
                )}
              </div>
            )})}
          </div>
          <button style={saveBtnStyle} onClick={saveSymptoms}>{t("Salvează simptome")}</button>
        </div>
      )}

      {open==="intercourse" && (
        <div style={{...card, position:"relative", overflow:"hidden", animation: unprotShake?"fmShake .5s":"none", background: unprotShake?"#2a1015":"#160f24"}}>
          {confetti && (
            <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:5,overflow:"hidden"}}>
              {/* 20 de explozii distribuite pe toată suprafața - sărbătoare! */}
              {Array.from({length:20}).map((_,b)=>{
                const bx=8+Math.random()*84, by=10+Math.random()*80;
                return Array.from({length:10}).map((_,i)=>{
                  const ang=(i/10)*Math.PI*2 + b; const dist=22+Math.random()*28;
                  const colors=["#ff3b3b","#ffd93b","#3bff6b","#3bd0ff","#c93bff","#ff8c3b","#ff3bd0","#fff","#7bff3b","#ff5b9c"];
                  return <span key={b+"-"+i} className="fm-particle" style={{left:`${bx}%`, top:`${by}%`, background:colors[(b+i)%colors.length], "--fx":`${Math.cos(ang)*dist}px`, "--fy":`${Math.sin(ang)*dist}px`, animationDelay:`${(b%6)*0.12+(i%3)*0.03}s`}}/>;
                });
              })}
            </div>
          )}
          {sadLeaf && (
            <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:5,overflow:"hidden"}}>
              {Array.from({length:18}).map((_,i)=>(
                <span key={i} className="fm-leaf" style={{left:`${Math.random()*92}%`, top:`${Math.random()*70}%`, fontSize:`${16+Math.random()*12}px`, "--lx":`${(Math.random()*80-40)}px`, "--ly":`${40+Math.random()*70}px`, "--lr":`${Math.random()*360-180}deg`, animationDelay:`${Math.random()*1}s`}}>🍂</span>
              ))}
            </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span className="lbl">{t("💕 Contact intim")}</span>
            <button className="bg" style={{fontSize:12,padding:"4px 10px"}} onClick={()=>setOpen(null)}>{t("Anulează")}</button>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>toggleProtection("protected")} style={bigBtn(icObj?.protection==="protected","linear-gradient(135deg,#22c55e,#15803d)","#4ade80", protPulse?{animation:"fmShieldPop .5s ease-out, fmShieldGlow .9s ease-out"}:null)}>
              <span style={{fontSize:30,lineHeight:1,display:"inline-block",animation:protPulse?"fmShieldPop .6s ease-out":"none"}}>🛡️</span><span style={{fontSize:13,fontWeight:600}}>{t("Protejat")}</span>
            </button>
            <button onClick={()=>toggleProtection("unprotected")} style={bigBtn(icObj?.protection==="unprotected","linear-gradient(135deg,#f97316,#dc2626)","var(--amber-2)")}>
              <span style={{fontSize:30,lineHeight:1}}>🔥</span><span style={{fontSize:13,fontWeight:600}}>{t("Neprotejat")}</span>
            </button>
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"8px 4px"}}>
            <span style={{fontSize:14,color:"var(--purple-2)"}}>{t("🔄 De câte ori")}</span>
            <div style={{display:"flex",alignItems:"center",gap:14}}>
              <button className="bg" style={{width:36,height:36,borderRadius:"50%",fontSize:20,padding:0}} onClick={()=>setCount((icObj?.count||1)-1)}>−</button>
              <span style={{fontSize:20,fontWeight:700,color:"#fff",minWidth:28,textAlign:"center"}}>{icObj?.count||1}</span>
              <button className="bg" style={{width:36,height:36,borderRadius:"50%",fontSize:20,padding:0}} onClick={()=>setCount((icObj?.count||1)+1)}>+</button>
            </div>
          </div>
          <div style={{display:"flex",gap:10,marginTop:10}}>
            <button onClick={()=>setOrgasm(true)} style={bigBtn(icObj?.orgasm===true,"linear-gradient(135deg,#ec4899,#be185d)","#f472b6")}>
              <span style={{fontSize:28,lineHeight:1}}>🎉</span><span style={{fontSize:12,fontWeight:600}}>{t("Cu orgasm")}</span>
            </button>
            <button onClick={()=>setOrgasm(false)} style={bigBtn(icObj?.orgasm===false,"linear-gradient(135deg,#6b7280,#4b5563)","var(--text-2)", icObj?.orgasm===false?{animation:"fmSigh .8s ease-out"}:null)}>
              <span style={{fontSize:28,lineHeight:1}}>😔</span><span style={{fontSize:12,fontWeight:600}}>{t("Fără orgasm")}</span>
            </button>
          </div>
          <button style={saveBtnStyle} onClick={saveIntercourse}>{t("Salvează contactul intim")}</button>
          {savedIc && (
            <button className="bg" style={{width:"100%",marginTop:8,fontSize:12,color:"#f0a0a0",display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onClick={deleteIntercourse}>{trash} {t("Șterge contactul intim")}</button>
          )}
          <button className="bg" style={{width:"100%",marginTop:8,fontSize:13,color:"var(--purple-2)",display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onClick={()=>setOpen("intercourseStats")}>{t("📊 Statistică activitate")}</button>
        </div>
      )}

      {open==="intercourseStats" && (()=>{
        const _isoOf=(dt)=>{ const y=dt.getFullYear(),m=String(dt.getMonth()+1).padStart(2,"0"),d=String(dt.getDate()).padStart(2,"0"); return `${y}-${m}-${d}`; };
        const _daysBetween=(a,b)=>{ const[y1,m1,d1]=a.split("-").map(Number); const[y2,m2,d2]=b.split("-").map(Number); return Math.round((new Date(y2,m2-1,d2)-new Date(y1,m1-1,d1))/(864e5)); };
        const allIc = data.intercourse || {};
        const entries = Object.entries(allIc).map(([d,v])=>({ iso:d, o: typeof v==="string"?{protection:v,count:1,orgasm:null}:v })).filter(e=>e.o);
        function statsForDays(nDays){
          let from=null;
          if(nDays!=null){ const t=new Date(); t.setDate(t.getDate()-nDays); from=_isoOf(t); }
          const inWin = entries.filter(e=> from==null || e.iso>=from );
          let total=0, prot=0, unprot=0, org=0;
          let minIso=null, maxIso=null;
          inWin.forEach(e=>{
            const c = e.o.count||1;
            total+=c;
            if(e.o.protection==="protected") prot+=c;
            if(e.o.protection==="unprotected") unprot+=c;
            if(e.o.orgasm===true) org+=c;
            if(!minIso||e.iso<minIso)minIso=e.iso;
            if(!maxIso||e.iso>maxIso)maxIso=e.iso;
          });
          let freqDays=null;
          if(total>0){
            let windowDays;
            if(nDays!=null){
              windowDays = nDays; // perioada selectată (1/3/6/12 luni)
            } else if(minIso){
              windowDays = _daysBetween(minIso, _isoOf(new Date()))+1; // total: de la primul contact până azi
            }
            if(windowDays && windowDays>0) freqDays = windowDays/total;
          }
          return {total,prot,unprot,org,freqDays};
        }
        const periods=[{label:"1 lună",days:30},{label:"3 luni",days:91},{label:"6 luni",days:182},{label:"12 luni",days:365},{label:"Total",days:null}];
        const s = statsForDays(statsWin);
        function freqText(f){
          if(f==null) return "—";
          const z=Math.round(f);
          if(z<=1) return t("în medie zilnic");
          return `${t("odată la")} ${z} ${t("zile")}`;
        }
        const statRow=(label,val,color)=>(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #2a1f3e"}}>
            <span style={{fontSize:13,color:"var(--purple-2)"}}>{typeof label==="string"?t(label):label}</span>
            <span style={{fontSize:15,fontWeight:700,color:color||"#fff"}}>{val}</span>
          </div>
        );
        return (
          <div style={card}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <span className="lbl">{t("📊 Statistică activitate")}</span>
              <button className="bg" style={{fontSize:12,padding:"4px 10px"}} onClick={()=>setOpen("intercourse")}>{t("← Înapoi")}</button>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
              {periods.map(p=>(
                <button key={p.label} onClick={()=>setStatsWin(p.days)} style={{
                  fontSize:12,padding:"6px 12px",borderRadius:16,cursor:"pointer",fontFamily:"inherit",
                  background: statsWin===p.days ? "#7c3aed" : "var(--bg)",
                  color: statsWin===p.days ? "#fff" : "var(--purple-2)",
                  border:"1px solid "+(statsWin===p.days?"#7c3aed":"var(--border-2)")
                }}>{t(p.label)}</button>
              ))}
            </div>
            {s.total===0 ? (
              <div style={{textAlign:"center",color:"#6b6480",fontSize:13,padding:"20px 0"}}>{t("Nicio activitate înregistrată în această perioadă.")}</div>
            ) : (
              <div>
                {statRow("💕 De câte ori", s.total, "#f472b6")}
                {statRow("🛡️ Protejat", s.prot, "#4ade80")}
                {statRow("🔥 Neprotejat", s.unprot, "var(--amber-2)")}
                {statRow("🎉 Cu orgasm", s.org, "var(--amber)")}
                {statRow("🔄 Frecvența", freqText(s.freqDays), "var(--purple)")}
              </div>
            )}
          </div>
        );
      })()}

      {open==="note" && (
        <div style={card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span className="lbl">{t("📝 Notiță")}</span>
            <button className="bg" style={{fontSize:12,padding:"4px 10px"}} onClick={()=>setOpen(null)}>{t("Anulează")}</button>
          </div>
          <textarea value={noteVal} onChange={e=>setNoteVal(e.target.value)} rows={3} placeholder={t("Scrie o notiță pentru această zi...")}
            style={{width:"100%",background:"var(--bg)",border:"1px solid var(--border-2)",borderRadius:8,color:"var(--text)",padding:"10px",fontSize:14,fontFamily:"inherit",resize:"none"}}/>
          <button style={saveBtnStyle} onClick={saveNoteSection}>{t("Salvează notița")}</button>
          {savedNote && (
            <button className="bg" style={{width:"100%",marginTop:8,fontSize:12,color:"#f0a0a0",display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onClick={deleteNote}>{trash} {t("Șterge notița")}</button>
          )}
        </div>
      )}
    </div>
  );
}
// ─── CALENDAR ÎNCEPUT / SFÂRȘIT MENSTRUAȚIE ─────────────────────────────────
// Mini-calendar pentru marcarea zilelor de menstruație, cu salvare și confirmare la ieșire
export function PeriodCalendar({initialDays, periodLen, mode, onSave, onClose}) {
  // initialDays: Set de ISO deja marcate; mode: "start" | "end"
  const [marked, setMarked] = React.useState(()=>new Set(initialDays||[]));
  const [dirty, setDirty] = React.useState(false);
  const [viewMonth, setViewMonth] = React.useState(()=>{ const n=new Date(); return {y:n.getFullYear(), m:n.getMonth()}; });
  const todayStr = (()=>{ const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`; })();
  function isoOf(y,m,d){ return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
  function toggleDay(iso){
    setDirty(true);
    setMarked(prev=>{
      const next=new Set(prev);
      if(next.has(iso)){
        next.delete(iso);
      } else {
        // La "start": când marchezi o zi nouă de început, completează automat lungimea menstruației
        if(mode==="start"){
          for(let i=0;i<(periodLen||5);i++) next.add(addDaysISO(iso,i));
        } else {
          next.add(iso);
        }
      }
      return next;
    });
  }
  function handleClose(){
    if(dirty){ fmConfirm(t("Închizi fără să salvezi? Modificările vor fi pierdute."),{okText:"Închide",cancelText:"Rămâi"}).then(ok=>{ if(ok) onClose(); }); return; }
    onClose();
  }
  function handleSave(){ onSave([...marked]); }
  const firstDow=(new Date(viewMonth.y,viewMonth.m,1).getDay()+6)%7;
  const daysInMonth=new Date(viewMonth.y,viewMonth.m+1,0).getDate();
  const cells=[]; for(let i=0;i<firstDow;i++)cells.push(null); for(let d=1;d<=daysInMonth;d++)cells.push(d);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:250,padding:16}}
      onClick={e=>{ if(e.target===e.currentTarget) handleClose(); }}>
      <div style={{background:"var(--cycle-card)",border:"1px solid #2a1e4a",borderRadius:18,padding:"18px 16px",width:"100%",maxWidth:380,maxHeight:"92vh",overflowY:"auto"}}>
        {/* header cu titlu, salvează, X */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontSize:16,fontWeight:700,color:"#fff"}}>
            {mode==="start"?t("Începutul menstruației"):t("Editează menstruația")}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={handleSave} style={{background:"linear-gradient(90deg,#e8849b,#c4607a)",color:"#fff",border:"none",borderRadius:18,padding:"6px 16px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>{t("Salvează")}</button>
            <button onClick={handleClose} style={{background:"none",border:"none",color:"#9b8fc0",cursor:"pointer",fontSize:22,lineHeight:1,padding:"0 2px"}}>✕</button>
          </div>
        </div>
        {/* nav lună */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <button style={{background:"none",border:"none",color:"var(--purple)",fontSize:22,cursor:"pointer",padding:"0 6px"}}
            onClick={()=>setViewMonth(v=>{const m=v.m-1;return m<0?{y:v.y-1,m:11}:{y:v.y,m};})}>‹</button>
          <div style={{fontWeight:700,fontSize:15,color:"#fff"}}>{t(MSHORT[viewMonth.m])}. {viewMonth.y}</div>
          <button style={{background:"none",border:"none",color:"var(--purple)",fontSize:22,cursor:"pointer",padding:"0 6px"}}
            onClick={()=>setViewMonth(v=>{const m=v.m+1;return m>11?{y:v.y+1,m:0}:{y:v.y,m};})}>›</button>
        </div>
        {/* antete zile */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4}}>
          {WDAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"#6b6080",fontWeight:700,padding:"2px 0"}}>{t(d)}</div>)}
        </div>
        {/* grid zile */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
          {cells.map((d,i)=>{
            if(d===null) return <div key={i} style={{aspectRatio:"1"}}/>;
            const iso=isoOf(viewMonth.y,viewMonth.m,d);
            const isMarked=marked.has(iso);
            const isToday=iso===todayStr;
            return (
              <button key={i} onClick={()=>toggleDay(iso)}
                style={{
                  aspectRatio:"1",borderRadius:"50%",cursor:"pointer",fontFamily:"inherit",
                  border: isToday?"2px solid #7c3aed":"1px solid transparent",
                  background: isMarked?"#e8607a":"transparent",
                  color: isMarked?"#fff":"var(--purple-2)",
                  fontSize:13,fontWeight:isMarked?700:500,
                  display:"flex",alignItems:"center",justifyContent:"center"
                }}>
                {d}
              </button>
            );
          })}
        </div>
        <div style={{marginTop:14,display:"flex",alignItems:"center",gap:6,fontSize:11,color:"#6b7080"}}>
          <span style={{display:"inline-block",width:12,height:12,borderRadius:"50%",background:"#e8607a"}}/>
          {t("Zile de menstruație")}
        </div>
      </div>
    </div>
  );
}

